import React, { useEffect, useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { TeacherDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { ReactComponent as ListEmptyStateIcon } from '../../../assets/list-empty-state-icon.svg';
import { GeneralStatusEnum, SortOrderEnum } from '../../../common/enums/common.enum';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { changeTeacherGroupEngagementStatus, createTeacherGroupEngagements, getTeacherGroupEngagements } from '../../../api/group.api';
import { TeacherEngagementDto, UnengagedTeacherDto } from '../../../common/interfaces/group.interface';
import { centeredContainerStyle } from '../../KindergartenDetails/components/LocationsAndGroupsTab/LocationsAndGroupsTab.styles';
import CreateTeacherGroupEngagementModal from './CreateTeacherGroupEngagementModal';
import { GroupTeacherTabSortKeyEnum } from '../../../common/enums/group.enum';

export const TEACHERS_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'Name', sortable: true, sortKey: GroupTeacherTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'Email', sortable: true, sortKey: GroupTeacherTabSortKeyEnum.EMAIL },
  { id: 3, value: '', label: 'Phone', sortable: true, sortKey: GroupTeacherTabSortKeyEnum.MOBILE_PHONE },
  { id: 4, value: '', label: 'Status', sortable: true, sortKey: GroupTeacherTabSortKeyEnum.STATUS }
];

const GroupTeachersTab: React.FC<{ groupId: string; kindergartenId: string; isGroupActive: boolean }> = ({
  groupId,
  kindergartenId,
  isGroupActive
}) => {
  const [teachers, setTeachers] = useState<TeacherEngagementDto[]>([]);
  const [loader, setLoader] = useState<boolean>(true);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);

  const [searchParams, setSearchParams] = useState<string>('');
  const [sortBy, setSortBy] = useState<GroupTeacherTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [teacherToDeactivate, setTeacherToDeactivate] = useState<TeacherDto | null>(null);

  const [isAddTeacherModalActive, setIsAddTeacherModalActive] = useState(false);

  useEffect(() => {
    loadTeachers();
  }, [groupId, metadata.currentPage, metadata.itemsPerPage, searchParams, sortBy, sortOrder]);

  const loadTeachers = async () => {
    setLoader(true);
    try {
      const result = await getTeacherGroupEngagements(
        groupId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams
      );
      setTeachers(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'An error occurred while fetching teachers');
    } finally {
      setLoader(false);
    }
  };

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    setSortBy(sortKey ? (sortKey as GroupTeacherTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : undefined);
  };

  const handleChangeTeacherStatus = async (teacherEngagementId: string) => {
    if (!kindergartenId) return;
    try {
      const response = await changeTeacherGroupEngagementStatus(groupId, teacherEngagementId);
      if (response.message) {
        createNotification(MESSAGE_TYPE.SUCCESS, response.message);
      }
      setTeachers(prev =>
        prev.map(t =>
          t.id === teacherEngagementId
            ? {
                ...t,
                status: t.status === GeneralStatusEnum.ACTIVE ? GeneralStatusEnum.INACTIVE : GeneralStatusEnum.ACTIVE
              }
            : t
        )
      );
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to change status for teacher');
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!teacherToDeactivate) return;
    await handleChangeTeacherStatus(teacherToDeactivate.id);
    setTeacherToDeactivate(null);
  };

  const handleCreateClick = async () => {
    setIsAddTeacherModalActive(true);
  };

  const handleCreateTeacherEngagements = async (newTeacherEngagements: UnengagedTeacherDto[]) => {
    try {
      const response = await createTeacherGroupEngagements(
        groupId,
        kindergartenId,
        newTeacherEngagements.map(item => item.id)
      );
      await loadTeachers();
      setIsAddTeacherModalActive(false);
      if (response.message) {
        createNotification(MESSAGE_TYPE.SUCCESS, response.message);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to create teacher engagements');
    }
  };

  return (
    <Box>
      {!teachers.length && !searchParams.length && !loader ? (
        <Box sx={centeredContainerStyle}>
          <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 'bold' }}>No teacher added yet</Typography>
          {isGroupActive && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              label="Create"
            />
          )}
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h2">Teachers</Typography>
            {isGroupActive && (
              <GenericButton
                buttonType={GenericButtonTypeEnum.Primary}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
                label="Add"
              />
            )}
          </Stack>
          <Box
            sx={{
              border: '1px solid #F0F0F0',
              borderRadius: 4
            }}
          >
            <TableHeader searchTitle="Search by name" sortOptions={[]} setSearchValue={handleSearchChange} />
            <Box>
              <BasicTable
                headings={TEACHERS_TABLE_HEADINGS}
                loader={loader}
                metadata={metadata}
                setMetadata={setMetadata}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
              >
                {teachers.map(teacher => (
                  <TableRow key={teacher.id}>
                    <TableCell>{teacher.fullName}</TableCell>
                    <TableCell>{teacher.email}</TableCell>
                    <TableCell>{teacher.phoneNumber ?? '-'}</TableCell>
                    <TableCell>
                      <GenericStatusSwitch
                        status={teacher.status}
                        disabled={!isGroupActive}
                        onChange={async () => {
                          if (teacher.status === GeneralStatusEnum.ACTIVE) {
                            setTeacherToDeactivate(teacher);
                          } else {
                            await handleChangeTeacherStatus(teacher.id);
                          }
                        }}
                        showStatusText={false}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {teacherToDeactivate && (
                  <GenericInfoModal
                    open={true}
                    onClose={() => setTeacherToDeactivate(null)}
                    onConfirm={handleConfirmDeactivation}
                    label="Deactivate teacher engagement"
                    message="Are you sure you want to deactivate "
                    confirmLabel="Deactivate"
                    entityName={teacherToDeactivate.fullName}
                  />
                )}
              </BasicTable>
            </Box>
          </Box>
        </>
      )}
      {isAddTeacherModalActive && (
        <CreateTeacherGroupEngagementModal
          onClose={() => setIsAddTeacherModalActive(false)}
          onConfirm={handleCreateTeacherEngagements}
          kindergartenId={kindergartenId}
          groupId={groupId}
        />
      )}
    </Box>
  );
};
export default GroupTeachersTab;
